import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Assessment as ReportIcon,
  Print as PrintIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import GirviPageShell from '../components/common/GirviPageShell';
import baseURL from '../baseURL';
import {
  fetchCompanyData,
  printDocument
} from '../utils/printUtility';
import StatutoryReportsPrint, {
  REPORT_TITLES
} from '../components/print/templates/StatutoryReportsPrint';

const dateKey = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const currentMonth = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: dateKey(first), to: dateKey(last) };
};

const eachDay = (from, to) => {
  const dates = [];
  const cursor = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  while (cursor <= end) {
    dates.push(dateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

const amount = (value) => Number(value || 0);
const pledgePrincipal = (pledge) => amount(pledge?.loanDetails?.principalAmount);
const releasePrincipal = (release) => amount(release?.calculations?.principle);
const releaseInterest = (release) => amount(release?.calculations?.interest);
const releaseId = (release) =>
  String(release?.pledgeId?._id || release?.pledgeId?.id || release?.pledgeId || '');

const sum = (rows, selector) => rows.reduce((total, row) => total + selector(row), 0);

const StatutoryReports = () => {
  const initial = useMemo(currentMonth, []);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);
  const [viewReport, setViewReport] = useState(null);

  const generateReports = async () => {
    if (!from || !to) {
      setError('Select both From and To dates.');
      return;
    }
    if (from > to) {
      setError('From date cannot be after To date.');
      return;
    }

    setLoading(true);
    setError('');
    setReportData(null);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const params = new URLSearchParams({ from, to });
      const [pledgeResponse, releaseResponse, daybookResponse, company] = await Promise.all([
        fetch(`${baseURL}/pledges/pledge-book`, { headers }),
        fetch(`${baseURL}/releases`, { headers }),
        fetch(`${baseURL}/accounting-entries/daybook?${params.toString()}`, { headers }),
        fetchCompanyData()
      ]);

      if (!pledgeResponse.ok) throw new Error('Could not load pledge records.');
      if (!releaseResponse.ok) throw new Error('Could not load release records.');

      const pledgeJson = await pledgeResponse.json();
      const releaseJson = await releaseResponse.json();
      const daybookJson = daybookResponse.ok ? await daybookResponse.json() : {};
      const allPledges = pledgeJson.data || [];
      const allReleases = (releaseJson.data || []).filter((row) => row.status !== 'cancelled');

      const periodPledges = allPledges
        .filter((row) => {
          const key = dateKey(row.date);
          return key >= from && key <= to;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      const periodReleases = allReleases
        .filter((row) => {
          const key = dateKey(row.releaseDate);
          return key >= from && key <= to;
        })
        .sort((a, b) => new Date(a.releaseDate) - new Date(b.releaseDate));

      const pledgesBefore = allPledges.filter((row) => dateKey(row.date) < from);
      const releasesBefore = allReleases.filter((row) => dateKey(row.releaseDate) < from);
      const openingOutstanding =
        sum(pledgesBefore, pledgePrincipal) - sum(releasesBefore, releasePrincipal);

      let runningOutstanding = openingOutstanding;
      const stockRows = eachDay(from, to).map((day) => {
        const dayPledges = periodPledges.filter((row) => dateKey(row.date) === day);
        const dayReleases = periodReleases.filter((row) => dateKey(row.releaseDate) === day);
        const loanGiven = sum(dayPledges, pledgePrincipal);
        const principalReceived = sum(dayReleases, releasePrincipal);
        const interestReceived = sum(dayReleases, releaseInterest);
        const opening = runningOutstanding;
        runningOutstanding = opening + loanGiven - principalReceived;
        return {
          date: day,
          opening,
          loanGiven,
          principalReceived,
          interestReceived,
          closing: runningOutstanding
        };
      });

      let cashRunning = amount(daybookJson.openingBalance);
      const cashRows = (daybookJson.rows || []).map((row) => {
        cashRunning += amount(row.credit) - amount(row.debit);
        return { ...row, balance: cashRunning };
      });
      const totalDebit = sum(cashRows, (row) => amount(row.debit));
      const totalCredit = sum(cashRows, (row) => amount(row.credit));

      const releasedBeforeIds = new Set(releasesBefore.map(releaseId).filter(Boolean));
      const releasedToDateIds = new Set(
        allReleases
          .filter((row) => dateKey(row.releaseDate) <= to)
          .map(releaseId)
          .filter(Boolean)
      );
      const openingCount = pledgesBefore.filter(
        (pledge) => !releasedBeforeIds.has(String(pledge._id || pledge.id))
      ).length;
      const eligibleToDate = allPledges.filter((pledge) => dateKey(pledge.date) <= to);
      const closingCount = eligibleToDate.filter(
        (pledge) => !releasedToDateIds.has(String(pledge._id || pledge.id))
      ).length;

      const periodLoanGiven = sum(periodPledges, pledgePrincipal);
      const periodPrincipalReceived = sum(periodReleases, releasePrincipal);
      const periodInterestReceived = sum(periodReleases, releaseInterest);
      const summary = {
        openingOutstanding,
        openingCount,
        periodLoanGiven,
        pledgeCount: periodPledges.length,
        totalLoans: openingOutstanding + periodLoanGiven,
        totalLoanCount: openingCount + periodPledges.length,
        periodPrincipalReceived,
        periodInterestReceived,
        periodTotalReceived: periodPrincipalReceived + periodInterestReceived,
        releaseCount: periodReleases.length,
        interestCount: periodReleases.filter((row) => releaseInterest(row) !== 0).length,
        closingOutstanding: runningOutstanding,
        closingCount
      };

      setReportData({
        from,
        to,
        company,
        pledges: periodPledges,
        releases: periodReleases,
        stockRows,
        cashBook: {
          openingBalance: amount(daybookJson.openingBalance),
          rows: cashRows,
          totalDebit,
          totalCredit,
          closingBalance: cashRunning
        },
        summary
      });
    } catch (err) {
      setError(err.message || 'Could not generate reports.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    generateReports();
    // Generate the current month once when the report page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const printReports = async (reportKeys) => {
    if (!reportData) return;
    setPrinting(true);
    try {
      await printDocument(
        'dayBook',
        { ...reportData, visibleReports: reportKeys },
        StatutoryReportsPrint
      );
    } finally {
      setPrinting(false);
    }
  };

  const reportMeta = reportData ? [
    { key: 'pledges', detail: `${reportData.summary.pledgeCount} pledge(s) created` },
    { key: 'releases', detail: `${reportData.summary.releaseCount} release(s)` },
    { key: 'interest', detail: `₹${reportData.summary.periodInterestReceived.toLocaleString('en-IN')} collected` },
    { key: 'stockBook', detail: `${reportData.stockRows.length} day(s)` },
    { key: 'cashBook', detail: `${reportData.cashBook.rows.length} transaction(s)` },
    { key: 'monthly', detail: 'Day-wise loan given and received' },
    { key: 'summary', detail: `${reportData.summary.closingCount} outstanding pledge(s)` }
  ] : [];

  return (
    <>
      <GirviPageShell
        title="Reports"
        subtitle="Statutory · Reports"
        chipLabel="Reports"
        maxWidth="xl"
       compact>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Select a period once, then view or print every money-lending report.
        </Typography>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              label="From Date"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="To Date"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button
              variant="contained"
              onClick={generateReports}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ReportIcon />}
            >
              Submit
            </Button>
            {reportData ? (
              <Button
                variant="contained"
                color="success"
                onClick={() => printReports(Object.keys(REPORT_TITLES))}
                disabled={printing}
                startIcon={<PrintIcon />}
              >
                Print All Reports
              </Button>
            ) : null}
          </Box>
        </Paper>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {loading ? (
          <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>
        ) : reportData ? (
          <Grid container spacing={2}>
            {reportMeta.map((report) => (
              <Grid item xs={12} sm={6} md={4} key={report.key}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1 }}>
                    <ReportIcon color="primary" sx={{ fontSize: 34, mb: 1 }} />
                    <Typography variant="h6">{REPORT_TITLES[report.key]}</Typography>
                    <Typography color="text.secondary">{report.detail}</Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end' }}>
                    <Tooltip title="View report">
                      <IconButton color="primary" onClick={() => setViewReport(report.key)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Print report">
                      <IconButton
                        color="success"
                        disabled={printing}
                        onClick={() => printReports([report.key])}
                      >
                        <PrintIcon />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : null}
      </GirviPageShell>

      <Dialog
        open={Boolean(viewReport)}
        onClose={() => setViewReport(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{viewReport ? REPORT_TITLES[viewReport] : ''}</DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: '#eee' }}>
          {viewReport && reportData ? (
            <StatutoryReportsPrint
              dayBookData={{ ...reportData, visibleReports: [viewReport] }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewReport(null)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            disabled={printing}
            onClick={() => printReports([viewReport])}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StatutoryReports;
